import { useEffect, useMemo, useState } from 'react'
import type {
  BacklogIssueConflict,
  BacklogMember,
  UpdateBacklogIssuePayload,
} from '@ai-meeting/shared'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  InfoIcon,
} from '@/components/Icons'
import { fmt, type UiStrings } from '@/constants/ui-locale'
import DateInput from '@/components/DateInput'
import {
  buildUpdatePayload,
  estimateDescriptionLines,
  FALLBACK_PRIORITIES,
  initialDraft,
  type ConfirmDraft,
  type ConfirmTicket,
} from '../utils/backlogConfirmDraft'

const FIELD_ROW = 'border-t border-[var(--border-color)]'
const LABEL_CELL = 'px-4 py-3 align-middle text-sm text-[var(--text-secondary)]'
// `break-words` because the column can no longer widen to fit: under a fixed
// layout a long title would run out of its cell instead of wrapping inside it.
const CURRENT_CELL = 'px-4 py-3 align-middle text-sm text-[var(--text-primary)] break-words'
const NEXT_CELL = 'px-4 py-3 align-middle'
// The description row alone hangs from the top: its right-hand cell is a
// four-row textarea, and centring a two-line summary against it leaves the
// two first lines nowhere near each other.
const LABEL_CELL_TOP = 'px-4 py-3 align-top text-sm text-[var(--text-secondary)]'
const CURRENT_CELL_TOP = 'px-4 py-3 align-top text-sm text-[var(--text-primary)] break-words'
const NEXT_CELL_TOP = 'px-4 py-3 align-top'

/** Lines of the current description shown before "Show all" has been pressed. */
const DESCRIPTION_CLAMP_LINES = 3
const CONTROL =
  'w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none'

/**
 * Asks before overwriting an issue the sync matched, and shows exactly what
 * would change (BRAI-575).
 *
 * Opened for a row the server reported as `matched`, which is the one verdict
 * that wrote nothing: Backlog has no delete API, so an overwrite is not
 * something to discover afterwards.
 *
 * Every field is editable, because the proposal comes from a match the server
 * is only confident about — and the reviewer is the one who can tell whether
 * this really is the same piece of work.
 *
 * @param issue The issue as Backlog has it right now.
 * @param members Project members, for the assignee picker. Empty disables it.
 * @param onApply Receives only the fields that differ from `issue`.
 * @param onSkip Leaves the issue alone and closes the panel.
 */
export default function BacklogConfirmPanel({
  ticket,
  proposed,
  members,
  priorities,
  conflicts,
  matchScore,
  matchReason,
  applying,
  error,
  onApply,
  onSkip,
  t,
}: {
  ticket: ConfirmTicket
  proposed: { summary: string; assigneeId: number | null; dueDate: string }
  members: BacklogMember[]
  /** Names this project accepts; empty falls back to Backlog's own four. */
  priorities: string[]
  conflicts: BacklogIssueConflict[]
  matchScore: number | null
  /** Only the model sets one — worth reading before agreeing to its match. */
  matchReason: string | null
  applying: boolean
  error: string | null
  onApply: (payload: UpdateBacklogIssuePayload) => void
  onSkip: () => void
  t: UiStrings
}) {
  const seed = useMemo(() => initialDraft(ticket, proposed), [ticket, proposed])
  const [draft, setDraft] = useState<ConfirmDraft>(seed)
  const [showFullDescription, setShowFullDescription] = useState(false)

  // A different row's panel reuses this component, and a draft left over from
  // the previous one would be offered as this issue's proposal.
  useEffect(() => {
    setDraft(seed)
    // Folded again with it: a description left open belongs to the ticket
    // that was on screen, not to the one that replaced it.
    setShowFullDescription(false)
  }, [seed])

  const currentAssignee = ticket.assigneeName ?? t.backlogConfirmUnassigned
  const nextAssignee = members.find((member) => member.id === draft.assigneeId)
  // The server refuses a write with a blocking conflict — a closed ticket, or
  // one edited since this was read. Offering the button anyway invites a press
  // that can only come back refused.
  const blocked = conflicts.some((conflict) => conflict.severity === 'blocking')
  const changed = {
    summary: draft.summary.trim() !== ticket.summary,
    priority: draft.priority !== (ticket.priority ?? ''),
    assignee: draft.assigneeId !== ticket.assigneeId,
    dueDate: draft.dueDate !== (ticket.dueDate ?? ''),
  }

  /** A row the write will touch is marked, so the eye lands on it first. */
  function rowClass(isChanged: boolean): string {
    return isChanged
      ? `${FIELD_ROW} border-l-[3px] border-l-[var(--accent)] bg-[var(--accent-light)]`
      : FIELD_ROW
  }

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-4 dark:border-amber-500/40 dark:bg-amber-500/5">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
        <ExclamationTriangleIcon className="h-4 w-4" />
        {fmt(t.backlogConfirmTitle, { key: ticket.issueKey })}
      </p>

      <p
        className="mt-1 flex items-center gap-1 text-xs text-[var(--text-secondary)]"
        title={fmt(t.backlogConfirmMatchHelp, { key: ticket.issueKey })}
      >
        {matchScore !== null && matchScore < 0.8
          ? t.backlogConfirmMatchLow
          : t.backlogConfirmMatchHigh}
        <InfoIcon className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
      </p>

      {matchReason && (
        // Only the model sets one, and it is the reader's best check on
        // whether this really is the same piece of work.
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          {fmt(t.backlogConfirmMatchReason, { reason: matchReason })}
        </p>
      )}

      {!ticket.isFresh && (
        // The values on the left came from a snapshot, so they may already be
        // out of date — and the overwrite is judged against them.
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{t.backlogConfirmStale}</p>
      )}

      {conflicts.map((conflict) => (
        <p
          key={`${conflict.field}-${conflict.message}`}
          className={`mt-2 text-xs ${
            conflict.severity === 'blocking' ? 'text-red-600' : 'text-amber-700'
          }`}
        >
          {/* The sentence is built upstream and is ready to show — rebuilding
              it here from `current`/`incoming` would say something subtly
              different from what the server refused on. */}
          {conflict.message}
        </p>
      ))}

      <div className="mt-3 overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)]">
        {/* Fixed rather than auto: the two value columns hold different kinds
            of thing — plain text on the left, form controls on the right — and
            an auto layout sizes by content, which gave the whole remainder to
            the text and squeezed the inputs. Under a fixed layout the columns
            left without a width share what is left of the row equally, so the
            before and after sit side by side at the same size. Only the header
            row's widths count, so this table's are declared there and nowhere
            else. */}
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-[var(--bg-tertiary)] text-left text-[11px] font-semibold text-[var(--text-secondary)]">
              <th className="w-40 px-4 py-2">{t.backlogConfirmColField}</th>
              <th className="px-4 py-2">{t.backlogConfirmColCurrent}</th>
              <th className="px-4 py-2">{t.backlogConfirmColNext}</th>
            </tr>
          </thead>
          <tbody>
            <tr className={rowClass(changed.summary)}>
              <td className={LABEL_CELL}>{t.backlogFieldSummary}</td>
              <td className={CURRENT_CELL}>{ticket.summary}</td>
              <td className={NEXT_CELL}>
                <input
                  className={CONTROL}
                  value={draft.summary}
                  aria-label={t.backlogFieldSummary}
                  disabled={applying}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, summary: event.target.value }))
                  }
                />
              </td>
            </tr>

            <tr className={`${FIELD_ROW} align-top`}>
              <td className={LABEL_CELL_TOP}>{t.backlogFieldDescription}</td>
              <td className={CURRENT_CELL_TOP}>
                {/* Three values, three sentences. `null` is not an empty
                    description — it is one nobody could read, and saying the
                    ticket is blank would be inventing an answer. */}
                {ticket.description === null ? (
                  <span className="text-xs italic text-[var(--text-tertiary)]">
                    {t.backlogConfirmDescriptionUnreadable}
                  </span>
                ) : ticket.description === '' ? (
                  <span className="text-xs italic text-[var(--text-tertiary)]">
                    {t.backlogConfirmDescriptionEmpty}
                  </span>
                ) : (
                  <>
                    <span
                      className={[
                        // `text-sm`, the size the textarea beside it uses: the
                        // two cells are the same description before and after,
                        // and reading them at different sizes makes the
                        // comparison harder than it needs to be.
                        'whitespace-pre-wrap text-sm',
                        // `block` only when expanded, and never alongside the
                        // clamp. Both declare `display`, `block` wins, and the
                        // clamp then does nothing at all while still looking
                        // present in the class list — measured in a browser:
                        // the box grew to the full height of the text.
                        showFullDescription ? 'block' : 'line-clamp-3',
                      ].join(' ')}
                    >
                      {ticket.description}
                    </span>
                    {estimateDescriptionLines(ticket.description) > DESCRIPTION_CLAMP_LINES && (
                      <button
                        type="button"
                        onClick={() => setShowFullDescription((shown) => !shown)}
                        className="mt-1 inline-flex items-center gap-0.5 text-xs font-semibold text-[var(--accent)] hover:underline"
                      >
                        {showFullDescription ? t.backlogConfirmShowLess : t.backlogConfirmShowAll}
                        {showFullDescription ? (
                          <ChevronUpIcon className="h-3 w-3" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </>
                )}
              </td>
              <td className={NEXT_CELL_TOP}>
                <textarea
                  rows={4}
                  className={`${CONTROL} resize-y`}
                  value={draft.description}
                  aria-label={t.backlogFieldDescription}
                  disabled={applying}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      description: event.target.value,
                      descriptionTouched: true,
                    }))
                  }
                />
              </td>
            </tr>

            <tr className={rowClass(changed.priority)}>
              <td className={LABEL_CELL}>{t.backlogFieldPriority}</td>
              <td className={CURRENT_CELL}>{ticket.priority ?? '—'}</td>
              <td className={NEXT_CELL}>
                <select
                  className={CONTROL}
                  value={draft.priority}
                  aria-label={t.backlogFieldPriority}
                  disabled={applying}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, priority: event.target.value }))
                  }
                >
                  {(priorities.length > 0 ? priorities : FALLBACK_PRIORITIES).map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            <tr className={rowClass(changed.assignee)}>
              <td className={`${LABEL_CELL} font-semibold text-[var(--text-primary)]`}>
                {t.backlogFieldAssignee}
              </td>
              <td className={CURRENT_CELL}>{currentAssignee}</td>
              <td className={NEXT_CELL}>
                <select
                  className={CONTROL}
                  value={draft.assigneeId ?? ''}
                  aria-label={t.backlogFieldAssignee}
                  disabled={applying || members.length === 0}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      assigneeId: event.target.value ? Number(event.target.value) : null,
                    }))
                  }
                >
                  <option value="">{t.backlogConfirmUnassigned}</option>
                  {/* The currently assigned person may not be in the member
                      page that was loaded, and dropping them would read as the
                      panel proposing to unassign the issue. */}
                  {ticket.assigneeId !== null &&
                    !members.some((member) => member.id === ticket.assigneeId) && (
                      <option value={ticket.assigneeId}>{currentAssignee}</option>
                    )}
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
                {nextAssignee?.mail_address && (
                  <span className="mt-1 block text-[11px] text-[var(--text-tertiary)]">
                    {nextAssignee.mail_address}
                  </span>
                )}
              </td>
            </tr>

            <tr className={rowClass(changed.dueDate)}>
              <td className={LABEL_CELL}>{t.backlogFieldDueDate}</td>
              <td className={CURRENT_CELL}>
                {/* The date format rather than a dash, so an empty cell reads
                    as a date nobody has set (BRAI-575 review). */}
                {ticket.dueDate ?? (
                  <span className="text-[var(--text-tertiary)]">{t.datePlaceholder}</span>
                )}
              </td>
              <td className={NEXT_CELL}>
                {/* The app's own picker, not `<input type="date">`. The native
                    one renders its text in the browser's locale — `mm/dd/yyyy`
                    on an en-US machine — and only opens its calendar from the
                    icon; this one is `dd/mm/yyyy` everywhere and opens from
                    anywhere in the field (BRAI-575 review). */}
                <DateInput
                  value={draft.dueDate}
                  onChange={(dueDate) => setDraft((current) => ({ ...current, dueDate }))}
                  ariaLabel={t.backlogFieldDueDate}
                  placeholder={t.datePlaceholder}
                  className={CONTROL}
                  disabled={applying}
                />
              </td>
            </tr>

          </tbody>
        </table>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border-color)] px-4 py-2.5">
          <span className="text-xs text-[var(--text-tertiary)]">{t.backlogConfirmEditHint}</span>
          <button
            type="button"
            onClick={() => setDraft(seed)}
            disabled={applying}
            className="rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] disabled:opacity-50"
          >
            {t.backlogConfirmReset}
          </button>
        </div>
      </div>

      {blocked && (
        // Says what happened; the conflict sentences above say why.
        <p role="alert" className="mt-2 text-xs font-semibold text-red-600">
          {t.backlogConfirmBlocked}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onApply(buildUpdatePayload(draft, ticket))}
          disabled={applying || blocked}
          title={blocked ? t.backlogConfirmBlocked : undefined}
          className="btn-primary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
        >
          {applying ? t.backlogConfirmApplying : t.backlogConfirmApply}
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={applying}
          className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] disabled:opacity-50"
        >
          {t.backlogConfirmSkip}
        </button>
      </div>
    </div>
  )
}
